import React from 'react';
import { Badge, Button, formatCurrency } from '@sandtech-hub-monorepo/ui-kit';
import { Info } from 'lucide-react';
import { GetSemesterPaymentPlansQuery } from '@sandtech-hub-monorepo/ehub-client';
import { Link } from 'react-router-dom';
import { formatDate } from '@sandtech-hub-monorepo/ui-kit-site';
import { IncompleteTransactionsSection } from '@sandtech-hub-monorepo/ui-kit-site';
import { ReceiptsSection } from '@sandtech-hub-monorepo/ui-kit-site';

interface Props {
  paymentPlan: GetSemesterPaymentPlansQuery['viewer']['paymentPlans'][number];
}

interface SemesterInfo {
  title: string;
  paidAmount: number;
  remainingAmount: number | null | undefined;
  isPreviousSemester?: boolean;
  isCurrentSemester?: boolean;
}

interface PaymentMessageParams {
  isPreviousSemester?: boolean;
  isCurrentSemester?: boolean;
  paidAmount: number;
  remainingAmount: number;
  amountPerInstalment: number;
  nextPaymentDate: string | null | undefined;
}

const getPaymentMessage = ({
  isPreviousSemester,
  isCurrentSemester,
  paidAmount,
  remainingAmount,
  amountPerInstalment,
  nextPaymentDate,
}: PaymentMessageParams): React.ReactNode => {
  const formattedDate = formatDate(nextPaymentDate);

  // Previous semester with unpaid balance
  if (isPreviousSemester && remainingAmount > 0) {
    return 'Please settle the remaining balance to pay for the current semester.';
  }

  // This semester with zero paid (starting fee scenario)
  if (isCurrentSemester && paidAmount === 0 && remainingAmount > 0) {
    return (
      <>
        Pay your{' '}
        <span className="font-semibold">
          {formatCurrency(amountPerInstalment)}
        </span>{' '}
        starting fee by <span className="font-semibold">{formattedDate}</span>{' '}
        to complete your enrollment and start semester.
      </>
    );
  }

  // This semester with unpaid balance (partially paid)
  if (isCurrentSemester && remainingAmount > 0) {
    return (
      <>
        Please pay the remaining balance before the end of the semester{' '}
        <span className="font-semibold">{formattedDate}</span>.
      </>
    );
  }

  // This semester paid in full
  if (isCurrentSemester && remainingAmount === 0) {
    return 'Your payments are on track.';
  }

  // Next semester with unpaid balance
  if (!isPreviousSemester && !isCurrentSemester && remainingAmount > 0) {
    return (
      <>
        Please pay the remaining balance before the start of next semester{' '}
        <span className="font-semibold">{formattedDate}</span>.
      </>
    );
  }

  return null;
};

const getMessageClassName = (
  isPreviousSemester?: boolean,
  isNearDueDate?: boolean
): string => {
  if (isPreviousSemester) return 'text-sm text-destructive';
  if (isNearDueDate) return 'text-sm text-warning';
  return 'text-sm text-muted-foreground';
};

interface PaymentInfoBoxProps {
  amountPerSemester: number;
  amountPerInstalment: number;
}

const PaymentInfoBox = ({
  amountPerSemester,
  amountPerInstalment,
}: PaymentInfoBoxProps) => (
  <div className="border border-info bg-info/10 rounded-lg p-6">
    <div className="flex gap-6 items-start">
      <Info className="stroke-info min-h-6 min-w-6" />
      <div className="flex flex-col gap-2">
        <p className="font-semibold text-base text-info">SET Payment Info</p>
        <p className="text-base text-info">
          You'll need to pay{' '}
          <span className="font-semibold">
            {formatCurrency(amountPerSemester)} per Semester
          </span>
          . To start the first semester, you'll need to pay at least a{' '}
          <span className="font-semibold">
            {formatCurrency(amountPerInstalment)} starting fee
          </span>
          . To move on to the next semester, you'll need to pay the{' '}
          <span className="font-semibold">
            {formatCurrency(amountPerInstalment)} starting fee and clear any
            outstanding balance
          </span>{' '}
          from the previous semester.
        </p>
      </div>
    </div>
  </div>
);

interface SemesterCardProps {
  semester: SemesterInfo;
  isFirst: boolean;
  isPaymentEnabled: boolean;
  isNearDueDate: boolean;
  amountPerInstalment: number;
  nextPaymentDate: string | null | undefined;
  viewUrl: string | null | undefined;
}

const SemesterCard = ({
  semester,
  isFirst,
  isPaymentEnabled,
  isNearDueDate,
  amountPerInstalment,
  nextPaymentDate,
  viewUrl,
}: SemesterCardProps) => {
  const { isPreviousSemester, isCurrentSemester } = semester;
  const remainingAmount = semester.remainingAmount ?? 0;
  const hasRemainingBalance = remainingAmount > 0;

  const message = getPaymentMessage({
    isPreviousSemester,
    isCurrentSemester,
    paidAmount: semester.paidAmount,
    remainingAmount,
    amountPerInstalment,
    nextPaymentDate,
  });

  const cardClassName = isFirst
    ? 'border-primary bg-gradient-to-tr from-transparent via-transparent to-primary/30'
    : 'border-muted bg-background';

  const titleClassName = isFirst
    ? 'font-medium text-xl text-foreground'
    : 'font-medium text-xl text-muted-foreground';

  const amountClassName = isFirst
    ? 'text-3xl text-foreground'
    : 'text-3xl text-muted-foreground';

  // Only apply warning/destructive colors when there's a remaining balance
  const shouldShowWarning = hasRemainingBalance && isNearDueDate;
  const balanceClassName = isFirst
    ? `text-3xl ${
        isPreviousSemester && hasRemainingBalance
          ? 'text-destructive'
          : shouldShowWarning
          ? 'text-warning'
          : 'text-foreground'
      }`
    : 'text-3xl text-muted-foreground';

  const showPaymentButton = isFirst
    ? viewUrl && hasRemainingBalance
    : isPaymentEnabled && viewUrl;

  // Only show warning styling on message when there's remaining balance
  const messageIsNearDueDate = hasRemainingBalance && isNearDueDate;

  return (
    <div
      className={`flex-1 border rounded-xl sm:p-8 p-4 max-w-2xl ${cardClassName}`}
    >
      <div className="flex flex-col gap-4">
        <p className={titleClassName}>{semester.title}</p>
        <div className="flex justify-between items-center sm:gap-8 gap-2">
          <div className="flex flex-col gap-1 items-center">
            <p className="sm:text-sm text-xs text-muted-foreground">
              Amount paid
            </p>
            <p className={amountClassName}>
              {formatCurrency(semester.paidAmount)}
            </p>
          </div>
          <div className="flex flex-col gap-1 items-center">
            <p className="sm:text-sm text-xs text-muted-foreground">
              Balance due
            </p>
            <p className={balanceClassName}>
              {formatCurrency(remainingAmount)}
            </p>
          </div>
          <div className="flex-1" />
          {showPaymentButton ? (
            <Link to={viewUrl!} target="_blank" rel="noopener noreferrer">
              <Button>Payment options</Button>
            </Link>
          ) : (
            !isFirst && <Button disabled>Payment options</Button>
          )}
        </div>
        {isFirst && message && (
          <p
            className={getMessageClassName(
              isPreviousSemester,
              messageIsNearDueDate
            )}
          >
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export const ActivePaymentsTab = ({ paymentPlan }: Props) => {
  const amountPerSemester = paymentPlan.amountPerSemester ?? 0;
  const viewUrl = paymentPlan.viewUrl;
  const nextPaymentDate = paymentPlan.nextPaymentDate;
  const amountPerInstalment = paymentPlan.amountPerInstalment ?? 0;
  const isInactive = paymentPlan.status === 'INACTIVE';
  const isCompleted = paymentPlan.status === 'COMPLETED';

  const isNearDueDate = nextPaymentDate
    ? new Date(nextPaymentDate).getTime() - Date.now() <= 48 * 60 * 60 * 1000
    : false;

  const semesters: SemesterInfo[] = [
    {
      title: 'Previous Semester',
      paidAmount:
        (paymentPlan.previousSemesterPaidInstalments ?? 0) *
        amountPerInstalment,
      remainingAmount: paymentPlan.previousSemesterRemainingAmount,
      isPreviousSemester: true,
    },
    {
      title: 'This Semester',
      paidAmount:
        (paymentPlan.currentSemesterPaidInstalments ?? 0) *
        amountPerInstalment,
      remainingAmount: paymentPlan.currentSemesterRemainingAmount,
      isCurrentSemester: true,
    },
    {
      title: 'Next Semester',
      paidAmount:
        (paymentPlan.nextSemesterPaidInstalments ?? 0) * amountPerInstalment,
      remainingAmount: paymentPlan.nextSemesterRemainingAmount,
    },
  ];

  const hasPreviousSemesterUnpaid =
    (paymentPlan.previousSemesterRemainingAmount ?? 0) > 0;

  const visibleSemesters = hasPreviousSemesterUnpaid
    ? [semesters[0], semesters[1]] // Previous + This Semester
    : [semesters[1], semesters[2]]; // This + Next Semester

  const firstSemesterHasBalance =
    (visibleSemesters[0]?.remainingAmount ?? 0) > 0;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex gap-4 items-center">
        <p className="font-semibold text-lg text-foreground">Payment status</p>
        <Badge
          variant="default"
          className={
            isInactive
              ? 'bg-muted text-muted-foreground border-muted'
              : 'bg-success/10 text-success border-success/20'
          }
        >
          {isCompleted ? 'Completed' : isInactive ? 'Inactive' : 'Active'}
        </Badge>
      </div>
      <div className="flex gap-6 flex-wrap">
        {visibleSemesters.map((semester, index) => (
          <SemesterCard
            key={semester.title}
            semester={semester}
            isFirst={index === 0}
            isPaymentEnabled={index === 0 || !firstSemesterHasBalance}
            isNearDueDate={isNearDueDate}
            amountPerInstalment={amountPerInstalment}
            nextPaymentDate={nextPaymentDate}
            viewUrl={viewUrl}
          />
        ))}
      </div>
      <PaymentInfoBox
        amountPerSemester={amountPerSemester}
        amountPerInstalment={amountPerInstalment}
      />
      <IncompleteTransactionsSection
        incompleteTransactions={paymentPlan?.incompleteTransactions || []}
      />
      <div>
        <p className="font-semibold text-lg">Payment History</p>
        <ReceiptsSection payments={paymentPlan?.payments || []} />
      </div>
    </div>
  );
};
